import type { RequestLeaveReviewType } from "views/RequestLeave/RequestLeaveReviewButtons"
import type {
  RequestActionStatistics,
  RequestStatisticDetailItem,
  RequestStatisticDetailSelection,
  UserRequestStatistics,
} from "views/RequestStatistics/types"

import { RequestLeaveAction, RequestLeaveStatus } from "__generated__/graphql"
import { useQuery } from "@apollo/client"
import { KeyboardArrowDownRounded, UnfoldMoreRounded } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AttendanceFilter } from "components/AgencySelect"
import {
  CurrentWorkCycleButton,
  DataViewFilterSurface,
  DateRangeFilterControls,
  FilterCollapseButton,
  Flex,
  FlexColumn,
  getDataViewTableStyles,
  GridEmpty,
  LoadingProgress,
  useDataViewTableScroll,
} from "components/common"
import { DateTime } from "luxon"
import { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  requestStatisticsPreferencesSelector,
  updateRequestStatisticsPreferences,
} from "reducers/dataViewPreferencesSlice"
import { profileSelector } from "reducers/profileSlice"
import ApproverForm from "views/RequestLeave/ApproverForm"
import { GET_USER_REQUEST_STATISTICS } from "views/RequestStatistics/graphql"
import RequestStatisticDetailDialog from "views/RequestStatistics/RequestStatisticDetailDialog"
import { REQUEST_STATISTICS_LAYOUT } from "views/RequestStatistics/styles"
import {
  canViewRequestStatistics,
  filterStatisticsByUsers,
  filterUsersWithRequests,
  getNextStatisticsSort,
  sortStatistics,
  StatisticsSortKey,
  StatisticsSortState,
  toStatisticsVariables,
} from "views/RequestStatistics/utils"

const formatStatistic = (value: number) => new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(value)

type RequestStatisticReviewSelection = {
  item: RequestStatisticDetailItem
  type: RequestLeaveReviewType
}

type StatisticsStatusChipProps = {
  color: "error" | "success" | "warning"
  count: number
  label: string
  onOpen: () => void
}

const StatisticsStatusChip = ({ color, count, label, onOpen }: StatisticsStatusChipProps) => {
  const hasData = count > 0

  return (
    <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      <Chip
        color={hasData ? color : "default"}
        label={label}
        onClick={hasData ? onOpen : undefined}
        size="small"
        variant="outlined"
      />
    </Box>
  )
}

type StatisticsCellProps = {
  onOpen: (status?: RequestStatisticDetailSelection["status"]) => void
  statistics: RequestActionStatistics
  unit: "lần" | "ngày"
}

const StatisticsCell = ({ onOpen, statistics, unit }: StatisticsCellProps) => {
  return (
    <Stack alignItems="center" gap={0.75}>
      <Typography color={statistics.total > 0 ? "text.primary" : "text.secondary"} variant="subtitle2">
        Tổng: {formatStatistic(statistics.total)} {unit}
      </Typography>
      <Flex className="flex-wrap" gap={0.5} justifyContent="center">
        <StatisticsStatusChip
          color="warning"
          count={statistics.awaitingApproval}
          label={`Chờ: ${formatStatistic(statistics.awaitingApproval)} ${unit}`}
          onOpen={() => onOpen(RequestLeaveStatus.AwaitingApproval)}
        />
        <StatisticsStatusChip
          color="success"
          count={statistics.approved}
          label={`Duyệt: ${formatStatistic(statistics.approved)} ${unit}`}
          onOpen={() => onOpen(RequestLeaveStatus.Approved)}
        />
        <StatisticsStatusChip
          color="error"
          count={statistics.rejected}
          label={`Từ chối: ${formatStatistic(statistics.rejected)} ${unit}`}
          onOpen={() => onOpen(RequestLeaveStatus.Rejected)}
        />
      </Flex>
    </Stack>
  )
}

const StatisticsTableCell = ({ onOpen, statistics, unit }: StatisticsCellProps) => {
  const hasData = statistics.total > 0
  const hasPendingApproval = statistics.awaitingApproval > 0
  const handleOpen = () => onOpen()

  return (
    <TableCell
      align="center"
      onClick={hasData ? handleOpen : undefined}
      onKeyDown={
        hasData
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return
              event.preventDefault()
              handleOpen()
            }
          : undefined
      }
      role={hasData ? "button" : undefined}
      sx={(theme) => ({
        "&:hover": hasData
          ? {
              bgcolor: hasPendingApproval
                ? alpha(
                    theme.palette.warning.main,
                    theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
                  )
                : "action.hover",
            }
          : undefined,
        bgcolor: hasPendingApproval
          ? alpha(theme.palette.warning.main, theme.palette.action.selectedOpacity)
          : undefined,
        cursor: hasData ? "pointer" : undefined,
        transition: theme.transitions.create("background-color", {
          duration: theme.transitions.duration.shortest,
        }),
      })}
      tabIndex={hasData ? 0 : undefined}
    >
      <StatisticsCell onOpen={onOpen} statistics={statistics} unit={unit} />
    </TableCell>
  )
}

const RequestStatisticsBoard = () => {
  const dispatch = useDispatch()
  const { user } = useSelector(profileSelector)
  const preferences = useSelector(requestStatisticsPreferencesSelector)
  const [sortState, setSortState] = useState<StatisticsSortState>({ direction: null, key: null })
  const [detailSelection, setDetailSelection] = useState<null | RequestStatisticDetailSelection>(null)
  const [reviewSelection, setReviewSelection] = useState<null | RequestStatisticReviewSelection>(null)
  const { onBodyScroll, tableHeadRef } = useDataViewTableScroll()

  const canView = canViewRequestStatistics(user?.position)
  const startDate = useMemo(() => DateTime.fromISO(preferences.startDate), [preferences.startDate])
  const endDate = useMemo(() => DateTime.fromISO(preferences.endDate), [preferences.endDate])
  const selectedAttendeeIds = useMemo(
    () => preferences.attendees.map((attendee) => attendee.id),
    [preferences.attendees],
  )
  const isDateRangeInvalid = !startDate.isValid || !endDate.isValid || endDate.startOf("day") < startDate.startOf("day")

  const { data, error, loading, refetch } = useQuery(GET_USER_REQUEST_STATISTICS, {
    skip: !canView || isDateRangeInvalid,
    variables: toStatisticsVariables(startDate.toFormat("yyyy-MM-dd"), endDate.toFormat("yyyy-MM-dd")),
  })

  const usersWithRequests = useMemo(() => filterUsersWithRequests(data?.getUserRequestStatistics ?? []), [data])
  const statistics = useMemo(() => {
    const filteredStatistics = filterStatisticsByUsers(usersWithRequests, selectedAttendeeIds)
    if (!sortState.key || !sortState.direction) return filteredStatistics
    return sortStatistics(filteredStatistics, sortState.key, sortState.direction)
  }, [selectedAttendeeIds, sortState, usersWithRequests])

  if (!canView) {
    return <Alert severity="error">Bạn không có quyền xem báo cáo yêu cầu.</Alert>
  }

  const handleSort = (nextSortKey: StatisticsSortKey) => {
    setSortState((current) => getNextStatisticsSort(current, nextSortKey))
  }

  const SortableHeader = ({ label, value }: { label: string; value: StatisticsSortKey }) => {
    const active = sortState.key === value && Boolean(sortState.direction)

    return (
      <TableSortLabel
        active={active}
        direction={active ? sortState.direction ?? "desc" : "desc"}
        IconComponent={active ? KeyboardArrowDownRounded : UnfoldMoreRounded}
        onClick={() => handleSort(value)}
        sx={(theme) => ({
          "& .MuiTableSortLabel-icon": {
            opacity: active ? 1 : theme.palette.action.disabledOpacity,
          },
        })}
      >
        {label}
      </TableSortLabel>
    )
  }

  const getHeaderSortDirection = (value: StatisticsSortKey) =>
    sortState.key === value ? sortState.direction ?? false : false

  const openDetails = (
    item: UserRequestStatistics,
    action: RequestStatisticDetailSelection["action"],
    status?: RequestStatisticDetailSelection["status"],
  ) => {
    setDetailSelection({
      action,
      status,
      userId: item.userId,
      userName: item.fullName || item.name,
    })
  }

  return (
    <FlexColumn flex={1} gap={2} minHeight={0}>
      <FlexColumn>
        <Flex alignItems="flex-start" className="flex-wrap" gap={2} justifyContent="space-between">
          <Box>
            <Typography variant="h5">Báo cáo yêu cầu</Typography>
            <Typography color="text.secondary" variant="body2">
              Số lần xin nghỉ, remote, về sớm và đi muộn theo thời gian áp dụng.
            </Typography>
          </Box>
          <FilterCollapseButton
            controlsId="request-statistics-filters"
            expanded={preferences.filtersExpanded}
            onClick={() =>
              dispatch(updateRequestStatisticsPreferences({ filtersExpanded: !preferences.filtersExpanded }))
            }
          />
        </Flex>

        <Collapse
          id="request-statistics-filters"
          in={preferences.filtersExpanded}
          sx={{ "&.MuiCollapse-hidden": { display: "none" } }}
        >
          <Box pt={2}>
            <DataViewFilterSurface
              headerAction={
                <CurrentWorkCycleButton
                  endDate={endDate}
                  onChange={({ endDate: nextEndDate, startDate: nextStartDate }) =>
                    dispatch(
                      updateRequestStatisticsPreferences({
                        endDate: nextEndDate.toFormat("yyyy-MM-dd"),
                        startDate: nextStartDate.toFormat("yyyy-MM-dd"),
                      }),
                    )
                  }
                  startDate={startDate}
                />
              }
              resultLabel={`${statistics.length} nhân viên`}
            >
              <Flex alignItems="center" className="flex-wrap" gap={1.5}>
                <DateRangeFilterControls
                  endDate={endDate}
                  onChange={(patch) =>
                    dispatch(
                      updateRequestStatisticsPreferences({
                        ...(patch.endDate ? { endDate: patch.endDate.toFormat("yyyy-MM-dd") } : {}),
                        ...(patch.startDate ? { startDate: patch.startDate.toFormat("yyyy-MM-dd") } : {}),
                      }),
                    )
                  }
                  onShiftPeriod={(delta) =>
                    dispatch(
                      updateRequestStatisticsPreferences({
                        endDate: endDate.plus({ months: delta }).toFormat("yyyy-MM-dd"),
                        startDate: startDate.plus({ months: delta }).toFormat("yyyy-MM-dd"),
                      }),
                    )
                  }
                  startDate={startDate}
                />
                <AttendanceFilter
                  attendees={preferences.attendees}
                  onChange={(attendees) => dispatch(updateRequestStatisticsPreferences({ attendees }))}
                />
              </Flex>
            </DataViewFilterSurface>
          </Box>
        </Collapse>
      </FlexColumn>

      {isDateRangeInvalid && <Alert severity="warning">Ngày kết thúc phải bằng hoặc sau ngày bắt đầu.</Alert>}
      {error && (
        <Alert
          action={
            <Button color="inherit" onClick={() => void refetch()} size="small">
              Thử lại
            </Button>
          }
          severity="error"
        >
          Không thể tải dữ liệu thống kê.
        </Alert>
      )}

      <TableContainer
        className="relative"
        component={Paper}
        sx={{ borderRadius: 2, flex: 1, minHeight: 0, overflow: "hidden" }}
        variant="outlined"
      >
        <Table
          sx={(theme) =>
            getDataViewTableStyles(theme, {
              gridTemplateColumns: REQUEST_STATISTICS_LAYOUT.tableGridColumns,
              minWidth: REQUEST_STATISTICS_LAYOUT.tableMinWidth,
            })
          }
        >
          <TableHead ref={tableHeadRef}>
            <TableRow>
              <TableCell align="center" sortDirection={getHeaderSortDirection("employee")}>
                <SortableHeader label="Nhân viên" value="employee" />
              </TableCell>
              <TableCell align="center" sortDirection={getHeaderSortDirection("leave")}>
                <SortableHeader label="Xin nghỉ" value="leave" />
              </TableCell>
              <TableCell align="center" sortDirection={getHeaderSortDirection("remote")}>
                <SortableHeader label="Remote" value="remote" />
              </TableCell>
              <TableCell align="center" sortDirection={getHeaderSortDirection("leaveEarly")}>
                <SortableHeader label="Về sớm" value="leaveEarly" />
              </TableCell>
              <TableCell align="center" sortDirection={getHeaderSortDirection("late")}>
                <SortableHeader label="Đi muộn" value="late" />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody onScroll={onBodyScroll}>
            {loading && (
              <TableRow sx={{ gridTemplateColumns: "1fr !important" }}>
                <TableCell>
                  <LoadingProgress loading />
                </TableCell>
              </TableRow>
            )}

            {!error && !loading && statistics.length === 0 && (
              <TableRow sx={{ gridTemplateColumns: "1fr !important" }}>
                <TableCell>
                  <GridEmpty />
                </TableCell>
              </TableRow>
            )}

            {statistics.map((item) => (
              <TableRow hover key={item.userId}>
                <TableCell align="center">
                  <Typography variant="subtitle2">{item.fullName || item.name}</Typography>
                  <Typography color="text.secondary" variant="caption">
                    {item.employeeCode || `User #${item.userId}`}
                  </Typography>
                </TableCell>
                <StatisticsTableCell
                  onOpen={(status) => openDetails(item, RequestLeaveAction.RequestLeave, status)}
                  statistics={item.leave}
                  unit="ngày"
                />
                <StatisticsTableCell
                  onOpen={(status) => openDetails(item, RequestLeaveAction.RequestRemote, status)}
                  statistics={item.remote}
                  unit="lần"
                />
                <StatisticsTableCell
                  onOpen={(status) => openDetails(item, RequestLeaveAction.RequestLeaveEarly, status)}
                  statistics={item.leaveEarly}
                  unit="lần"
                />
                <StatisticsTableCell
                  onOpen={(status) => openDetails(item, RequestLeaveAction.RequestLate, status)}
                  statistics={item.late}
                  unit="lần"
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <RequestStatisticDetailDialog
        endDate={endDate.toFormat("yyyy-MM-dd")}
        onClose={() => setDetailSelection(null)}
        onReview={(item, type) => {
          setDetailSelection(null)
          setReviewSelection({ item, type })
        }}
        open={Boolean(detailSelection)}
        selection={detailSelection}
        startDate={startDate.toFormat("yyyy-MM-dd")}
      />
      {reviewSelection && (
        <ApproverForm
          actionType={reviewSelection.type}
          onClose={() => setReviewSelection(null)}
          open
          refetchQueries={[GET_USER_REQUEST_STATISTICS]}
          selectedLeave={reviewSelection.item}
        />
      )}
    </FlexColumn>
  )
}

export default RequestStatisticsBoard
